import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { authService } from '../services/authService/authService';
import { setActivityCallback } from '../services/instance';

interface SessionContextType {
  showSessionModal: boolean;
  setShowSessionModal: (show: boolean) => void;
  showWarningModal: boolean;
  setShowWarningModal: (show: boolean) => void;
  extendSession: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes
const WARNING_TIME = 2 * 60 * 1000; // 2 minutes before expiry

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const resetTimers = () => {
    try {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      setShowWarningModal(false);
    } catch (error) {
      console.error('Error resetting timers:', error);
    }
  };

  const startTimers = () => {
    resetTimers();
    lastActivityRef.current = Date.now();

    // Set warning timer (58 minutes)
    warningTimerRef.current = setTimeout(() => {
      setShowWarningModal(true);
    }, INACTIVITY_TIMEOUT - WARNING_TIME);

    // Set expiry timer (60 minutes)
    inactivityTimerRef.current = setTimeout(() => {
      handleSessionExpiry();
    }, INACTIVITY_TIMEOUT);
  };

  const handleSessionExpiry = async () => {
    try {
      setShowWarningModal(false);
      setShowSessionModal(true);
      await authService.signOut();
    } catch (error) {
      console.error('Error handling session expiry:', error);
      // Fallback: clear storage and redirect
      localStorage.clear();
      window.location.href = '/session-timeout';
    }
  };

  const extendSession = () => {
    setShowWarningModal(false);
    startTimers();
  };

  const handleActivity = () => {
    try {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;

      // Only reset if more than 1 minute has passed to avoid excessive resets
      if (timeSinceLastActivity > 60000) {
        startTimers();
      }
    } catch (error) {
      console.error('Error handling activity:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    startTimers();

    // Set up API activity tracking
    setActivityCallback(handleActivity);

    // Activity event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      resetTimers();
      setActivityCallback(() => {});
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, []);

  return (
    <SessionContext.Provider
      value={{
        showSessionModal,
        setShowSessionModal,
        showWarningModal,
        setShowWarningModal,
        extendSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
